/**
 * Subscribe to an operation stream and fold its events into state with a
 * pure reducer (docs/design/web/WEB_STREAMING_DESIGN.md).
 *
 *   const run = useOperationStream(opId, chatStreamReducer, initialChatStream, {
 *     onSettled: () => queryClient.invalidateQueries(...),
 *   });
 *   run.state; run.status; run.stalled; run.cancel();
 *
 * The hook owns subscribe/resume/cleanup and batches state updates to one
 * per animation frame (token deltas arrive far faster than React needs to
 * render). Reducers own meaning: they're plain functions, unit-tested from
 * event lists.
 */

import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { ApiError, cancelOperation, type StreamEvent, streamOperation } from "@/api";

export type StreamStatus = "idle" | "streaming" | "completed" | "cancelled" | "failed";

/**
 * How long Stop waits for the backend to confirm (a live run does within a second or two) before the operation
 * ends here anyway: a run whose worker died never confirms, and its stream only gets keepalives (MHS-863).
 */
export const STOP_GRACE_MS = 5_000;
/** No event for this long and the operation counts as stalled (the old UI's "may be stale" after 5 minutes). */
export const STALL_AFTER_MS = 5 * 60_000;

export interface OperationStreamResult<S> {
  state: S;
  status: StreamStatus;
  error: ApiError | null;
  /** No event for STALL_AFTER_MS while streaming (keepalives don't count): the work may have stopped. */
  stalled: boolean;
  /**
   * Stop the WORK (backend cancel), not just the subscription. Once the backend has taken the Stop, the operation
   * ends here as cancelled (and the stream is closed) if that isn't confirmed on the stream within STOP_GRACE_MS.
   */
  cancel: () => Promise<void>;
}

interface Options<S> {
  /**
   * Called once when the operation reaches a terminal event (or the stream
   * fails, or a Stop isn't confirmed in time), with the final reduced state
   * (React may not have rendered it yet).
   */
  onSettled?: (status: StreamStatus, finalState: S) => void;
  /** Test seam: frame scheduler (defaults to requestAnimationFrame). */
  schedule?: (flush: () => void) => void;
  /** Test seam: defaults to STOP_GRACE_MS. */
  stopGraceMs?: number;
  /** Test seam: defaults to STALL_AFTER_MS. */
  stallAfterMs?: number;
}

const defaultSchedule = (flush: () => void) =>
  typeof requestAnimationFrame === "function" ? requestAnimationFrame(() => flush()) : setTimeout(flush, 16);

/**
 * Ask the backend to stop `opId`. True when it took the Stop, or has nothing left to stop (the operation is gone or
 * already over); false when the request failed or this kind of operation can't be stopped.
 */
async function stopTaken(opId: string): Promise<boolean> {
  try {
    await cancelOperation({ path: { op_id: opId }, throwOnError: true });
    return true;
  } catch (error) {
    // Every failed call is an ApiError (src/api/client.ts).
    const { status, code } = error as ApiError;
    return status === 404 || code === "OPERATION_FINISHED";
  }
}

function statusFor(event: StreamEvent): StreamStatus | null {
  if (event.event === "operation_failed") return "failed";
  if (event.event === "operation_completed") return event.data.cancelled ? "cancelled" : "completed";
  return null;
}

export function useOperationStream<S>(
  opId: string | null,
  reducer: (state: S, event: StreamEvent) => S,
  initialState: S,
  {
    onSettled,
    schedule = defaultSchedule,
    stopGraceMs = STOP_GRACE_MS,
    stallAfterMs = STALL_AFTER_MS,
  }: Options<S> = {},
): OperationStreamResult<S> {
  const [state, dispatch] = useReducer(
    // `null` starts over from the initial state: the stream is being replayed from the beginning (stream.ts onReset).
    (s: S, events: (StreamEvent | null)[]) =>
      events.reduce((acc, event) => (event === null ? initialState : reducer(acc, event)), s),
    initialState,
  );
  const [status, setStatus] = useState<StreamStatus>(opId ? "streaming" : "idle");
  const [error, setError] = useState<ApiError | null>(null);
  const [stalled, setStalled] = useState(false);
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;
  // Read through refs inside the subscription so callers can pass inline values.
  const reducerRef = useRef(reducer);
  reducerRef.current = reducer;
  const initialRef = useRef(initialState);
  // Stops the running operation; null once it has ended.
  const stopRef = useRef<(() => Promise<void>) | null>(null);

  useEffect(() => {
    if (!opId) return;
    const controller = new AbortController();
    let pending: (StreamEvent | null)[] = [];
    let scheduled = false;
    // Mirror of the reduced state, so onSettled sees the final state without waiting for a render.
    let latest = initialRef.current;
    let lastEventAt = Date.now();
    let isStalled = false;
    let stallTimer: ReturnType<typeof setTimeout> | undefined;
    let graceTimer: ReturnType<typeof setTimeout> | undefined;
    let stopping = false;
    let finished = false; // settled, or the page left it
    const flush = () => {
      scheduled = false;
      if (pending.length && !controller.signal.aborted) dispatch(pending);
      pending = [];
    };
    // Checked again when due rather than reset per event: deltas arrive far faster than a timer needs re-arming.
    const watchStall = (delay: number) => {
      stallTimer = setTimeout(() => {
        const idle = Date.now() - lastEventAt;
        if (idle < stallAfterMs) {
          watchStall(stallAfterMs - idle);
        } else {
          isStalled = true;
          setStalled(true);
        }
      }, delay);
    };
    // An event, or `null` to start over. Batched, so a reset shows together with the replay that follows it.
    const queue = (event: StreamEvent | null) => {
      pending.push(event);
      latest = event === null ? initialRef.current : reducerRef.current(latest, event);
      lastEventAt = Date.now();
      if (isStalled) {
        isStalled = false;
        setStalled(false);
        watchStall(stallAfterMs);
      }
      if (!scheduled) {
        scheduled = true;
        schedule(flush);
      }
    };
    const settle = (final: StreamStatus) => {
      finished = true;
      stopRef.current = null;
      clearTimeout(stallTimer);
      clearTimeout(graceTimer);
      flush();
      setStatus(final);
      setStalled(false);
      onSettledRef.current?.(final, latest);
    };
    stopRef.current = async () => {
      if (stopping) return; // one request at a time
      stopping = true;
      // Not taken (the request failed): the work goes on, and Stop can be pressed again.
      if (!(await stopTaken(opId))) {
        stopping = false;
        return;
      }
      if (finished) return;
      // The result normally arrives on the stream (operation_completed {cancelled: true}).
      graceTimer = setTimeout(() => {
        // Not confirmed in time (its worker may be gone): end it here, then stop listening. In that order:
        // flush dispatches nothing once the request is aborted, which would lose the last text received.
        settle("cancelled");
        controller.abort();
      }, stopGraceMs);
    };
    setStatus("streaming");
    setError(null);
    setStalled(false);
    watchStall(stallAfterMs);

    void (async () => {
      let final: StreamStatus = "failed";
      try {
        for await (const event of streamOperation(opId, { signal: controller.signal, onReset: () => queue(null) })) {
          queue(event);
          final = statusFor(event) ?? final;
        }
      } catch (e) {
        setError(e instanceof ApiError ? e : new ApiError(0, String(e)));
      }
      if (!controller.signal.aborted) settle(final);
    })();

    return () => {
      finished = true;
      stopRef.current = null;
      clearTimeout(stallTimer);
      clearTimeout(graceTimer);
      controller.abort();
    };
  }, [opId, schedule, stopGraceMs, stallAfterMs]);

  const cancel = useCallback(async () => {
    await stopRef.current?.();
  }, []);

  return { state, status, error, stalled, cancel };
}
